import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Chip,
  Stack,
  Tooltip
} from '@mui/material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import MapIcon from '@mui/icons-material/Map';
import EventIcon from '@mui/icons-material/Event';
import FlightIcon from '@mui/icons-material/Flight';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Interface for locations to display on the map
interface MapLocation {
  id: string;
  name: string;
  type: 'trip' | 'event';
  coordinates: [number, number];
  description?: string;
  startDate?: Date;
  endDate?: Date;
  imageUrl?: string;
}

const OrganizationMapView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { activeOrganization, isPersonalAccount, switchToOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<MapLocation[]>([]);
  const [filters, setFilters] = useState<string[]>(['trips', 'events']);
  
  // Map refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const bounds = useRef<mapboxgl.LngLatBounds | null>(null);

  // Helper function to validate coordinates
  const isValidCoordinate = (coord: [number, number]): boolean => {
    if (!Array.isArray(coord) || coord.length !== 2) return false;
    const [lng, lat] = coord;
    // Check if numbers and within valid ranges
    return (
      typeof lng === 'number' && 
      typeof lat === 'number' && 
      !isNaN(lng) && 
      !isNaN(lat) && 
      lng >= -180 && 
      lng <= 180 && 
      lat >= -85 && 
      lat <= 85
    );
  };

  // Handle filter changes
  const handleFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilters: string[],
  ) => {
    if (newFilters.length > 0) {
      setFilters(newFilters);
    }
  };

  // Effect to fetch organization's trips and events 
  useEffect(() => {
     // If we have an organization ID but we're in personal account mode,
     // switch to the organization
     if (id && isPersonalAccount) {
        switchToOrganization(id);
     }

     const fetchLocations = async () => {
        try {
           setLoading(true);

           // Create an array to store all locations
           const allLocations: MapLocation[] = [];

           // Fetch trips for this organization
           const tripsRef = collection(db, 'trips');
           const tripsQuery = query(
              tripsRef,
              where('organizationId', '==', id),
              orderBy('startDate', 'desc')
           );

           const tripsSnapshot = await getDocs(tripsQuery);

           // Process each trip
           tripsSnapshot.forEach((doc) => {
              const tripData = doc.data();

              // Check for accommodations with coordinates
              if (tripData.accommodations && tripData.accommodations.length > 0) {
                 tripData.accommodations.forEach((accommodation: any) => {
                    // Get coordinates (either directly or from lat/lng)
                    let coordinates: [number, number] | null = null;

                    if (accommodation.coordinates) {
                       coordinates = accommodation.coordinates;
                    } else if (
                       typeof accommodation.latitude === 'number' &&
                       typeof accommodation.longitude === 'number'
                    ) {
                       coordinates = [accommodation.longitude, accommodation.latitude];
                    }

                    if (coordinates && isValidCoordinate(coordinates)) {
                       allLocations.push({
                          id: `${doc.id}-accommodation-${accommodation.id || Date.now()}`,
                          name: accommodation.name || tripData.title,
                          type: 'trip',
                          coordinates,
                          description: `${tripData.title} - ${accommodation.name || 'Accommodation'}`,
                          startDate: tripData.startDate?.toDate(),
                          endDate: tripData.endDate?.toDate(),
                          imageUrl: tripData.coverImageUrl,
                       });
                    }
                 });
              }

              // Check for trip locations with coordinates
              if (tripData.locations && tripData.locations.length > 0) {
                 tripData.locations.forEach((location: any) => {
                    // Get coordinates (either directly or from lat/lng)
                    let coordinates: [number, number] | null = null;

                    if (location.coordinates) {
                       coordinates = location.coordinates;
                    } else if (
                       typeof location.latitude === 'number' &&
                       typeof location.longitude === 'number'
                    ) {
                       coordinates = [location.longitude, location.latitude];
                    }

                    if (coordinates && isValidCoordinate(coordinates)) {
                       allLocations.push({
                          id: `${doc.id}-location-${location.id || Date.now()}`,
                          name: location.name || tripData.title,
                          type: 'trip',
                          coordinates,
                          description: `${tripData.title} - ${location.name || 'Location'}`,
                          startDate: tripData.startDate?.toDate(),
                          endDate: tripData.endDate?.toDate(),
                          imageUrl: tripData.coverImageUrl,
                       });
                    }
                 });
              }

              // Check for primary destination
              if (tripData.primaryDestination && tripData.primaryDestination.coordinates) {
                 const coordinates = tripData.primaryDestination.coordinates;

                 if (isValidCoordinate(coordinates)) {
                    allLocations.push({
                       id: `${doc.id}-primary`,
                       name: tripData.destination || tripData.title,
                       type: 'trip',
                       coordinates,
                       description: `${tripData.title} - ${tripData.destination || 'Destination'}`,
                       startDate: tripData.startDate?.toDate(),
                       endDate: tripData.endDate?.toDate(),
                       imageUrl: tripData.coverImageUrl,
                    });
                 }
              }
           });

           // Fetch events for this organization
           const eventsRef = collection(db, 'events');
           const eventsQuery = query(
              eventsRef,
              where('organizationId', '==', id),
              orderBy('startDate', 'desc')
           );

           const eventsSnapshot = await getDocs(eventsQuery);

           // Process each event
           eventsSnapshot.forEach((doc) => {
              const eventData = doc.data();

              // Get coordinates (either directly or from lat/lng)
              let coordinates: [number, number] | null = null;

              if (eventData.location && eventData.location.coordinates) {
                 coordinates = eventData.location.coordinates;
              } else if (
                 eventData.location &&
                 typeof eventData.location.latitude === 'number' &&
                 typeof eventData.location.longitude === 'number'
              ) {
                 coordinates = [eventData.location.longitude, eventData.location.latitude];
              }

              if (coordinates && isValidCoordinate(coordinates)) {
                 allLocations.push({
                    id: doc.id,
                    name: eventData.title,
                    type: 'event',
                    coordinates,
                    description: eventData.description,
                    startDate: eventData.startDate?.toDate(),
                    endDate: eventData.endDate?.toDate(),
                    imageUrl: eventData.coverImageUrl,
                 });
              }
           });

           // Set the locations state
           setLocations(allLocations);

           setLoading(false);
        } catch (err) {
           console.error('Error fetching location data:', err);
           setError('Failed to load location data. Please try again later.');
           setLoading(false);
        }
     };

     if (id) {
        fetchLocations();
     }
  }, [id, isPersonalAccount, switchToOrganization]);

  // Initialize map when component mounts
  useEffect(() => {
     if (!mapContainer.current) return;

     // Clean up function to remove map instance when component unmounts
     return () => {
        if (map.current) {
           // First, clear all markers
           markers.current.forEach((marker) => marker.remove());
           markers.current = [];

           // Then remove the map
           map.current.remove();
           map.current = null;
        }
     };
  }, []);

  // Effect to initialize or update map when locations change or filters change
  useEffect(() => {
     // If component is not mounted or being unmounted, don't proceed
     if (!mapContainer.current || loading) return;

     const setupMap = async () => {
        try {
           // Log setup

           // Set Mapbox token
           const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
           if (!token) {
              setError('Mapbox token not found. Map features disabled.');
              return;
           }

           mapboxgl.accessToken = token;

           // Clean up any previous map instance
           if (map.current) {
              // Clear existing markers
              markers.current.forEach((marker) => marker.remove());
              markers.current = [];

              // Remove the map
              map.current.remove();
              map.current = null;
           }

           // Set initial center and zoom
           let initialCenter: [number, number] = [-98.5795, 39.8283]; // Default to center of US
           let initialZoom = 3; // Lower zoom to show broader area

           // Create a bounds object for all locations
           bounds.current = new mapboxgl.LngLatBounds();

           // Create map
           map.current = new mapboxgl.Map({
              container: mapContainer.current as HTMLElement,
              style: 'mapbox://styles/mapbox/streets-v11',
              center: initialCenter,
              zoom: initialZoom,
           });

           // Add navigation controls
           map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

           // Filter locations based on current filter settings
           const filteredLocations = locations.filter((location) => {
              if (location.type === 'trip' && filters.includes('trips')) return true;
              if (location.type === 'event' && filters.includes('events')) return true;
              return false;
           });

           // Wait for map to load before adding markers
           map.current.on('load', () => {
              if (!map.current) return;

              // Add markers for each location
              filteredLocations.forEach((location) => {
                 if (!map.current) return;

                 // Create a popup for the marker
                 const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
              <div>
                <h3>${location.name}</h3>
                <p>${location.description || ''}</p>
                ${location.startDate ? `<p>Date: ${location.startDate.toLocaleDateString()}</p>` : ''}
                ${location.imageUrl ? `<img src="${location.imageUrl}" alt="${location.name}" style="max-width: 100%; max-height: 100px; object-fit: cover;" />` : ''}
              </div>
            `);

                 // Create element for custom marker
                 const el = document.createElement('div');
                 el.className = 'custom-marker';
                 el.style.backgroundColor = location.type === 'trip' ? '#3f51b5' : '#f50057';
                 el.style.borderRadius = '50%';
                 el.style.width = '30px';
                 el.style.height = '30px';
                 el.style.display = 'flex';
                 el.style.justifyContent = 'center';
                 el.style.alignItems = 'center';
                 el.style.color = 'white';
                 el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
                 el.innerHTML =
                    location.type === 'trip'
                       ? '<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="white"><path d="M0 0h24v24H0z" fill="none"/><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>'
                       : '<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="white"><path d="M0 0h24v24H0z" fill="none"/><path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/></svg>';

                 // Create marker
                 const marker = new mapboxgl.Marker(el)
                    .setLngLat(location.coordinates)
                    .setPopup(popup)
                    .addTo(map.current);

                 // Store marker for later cleanup
                 markers.current.push(marker);

                 // Extend bounds to include this location
                 bounds.current?.extend(location.coordinates);
              });

              // Fit map to bounds if we have locations
              if (filteredLocations.length > 0 && bounds.current && map.current) {
                 map.current.fitBounds(bounds.current, {
                    padding: 50,
                    maxZoom: 15,
                 });
              }
           });
        } catch (err) {
           console.error('Error setting up map:', err);
           setError('Failed to initialize map. Please try again later.');
        }
     };

     setupMap();
  }, [locations, filters]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {activeOrganization?.name || 'Organization'} Map
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View all locations and trips associated with this organization
            </Typography>
          </Box>
          
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip 
                icon={<LocationOnIcon />} 
                label={`${locations.length} Locations`} 
                color="primary" 
                variant="outlined" 
              />
              
              <ToggleButtonGroup
                value={filters}
                onChange={handleFilterChange}
                aria-label="map filters"
                size="small"
              >
                <ToggleButton value="trips" aria-label="show trips">
                  <Tooltip title="Show Trips">
                    <FlightIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="events" aria-label="show events">
                  <Tooltip title="Show Events">
                    <EventIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Box>
        </Box>
      </Paper>
      
      {/* Stats bar */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={3} justifyContent="space-around">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {locations.filter(l => l.type === 'trip').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Trip Locations
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="secondary">
              {locations.filter(l => l.type === 'event').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Event Locations
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="info.main">
              {new Set(locations.map(l => l.name)).size}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unique Places
            </Typography>
          </Box>
        </Stack>
      </Paper>
      
      {/* Map container */}
      <Paper 
        sx={{ 
          height: 'calc(100vh - 250px)', 
          minHeight: '500px',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {locations.length === 0 ? (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              color: 'text.secondary'
            }}
          >
            <MapIcon sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" component="div">
              No Locations Found
            </Typography>
            <Typography variant="body2">
              Add trips or events with locations to see them on the map
            </Typography>
          </Box>
        ) : (
          <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
        )}
      </Paper>
    </Container>
  );
};

export default OrganizationMapView;